import { useEffect, useState } from 'react';

import { api, type EtteremLista, type EtteremSor } from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Étterem keresése a teljes törzsben, név és cím alapján.
 *
 * A találatra kattintva kártya nyílik, ahol rögtön megadható a mennyiség és a
 * fizetési mód — így egy mozdulattal felvehető a cím, nem kell külön képernyőre
 * menni érte. Ugyanezt használja a járatszervezés és az állandó címek is.
 */

export interface KeresoErtekek {
  mo: string;
  fo: string;
  /** Kihelyezendő hordó — csak a járatszervezésen. */
  hordo: string;
  mennyiseg: string;
  /** F+ olaj-e — csak a járatszervezésen, és csak ha a partnernek van F+ ára. */
  fplusz: boolean;
  fizetesi_mod: string;
}

const URES: Omit<KeresoErtekek, 'fizetesi_mod'> = {
  mo: '',
  fo: '',
  hordo: '',
  mennyiseg: '',
  fplusz: false,
};

export function EtteremKereso({
  fizetesiLehetosegek,
  gombCimke,
  gombStilus = 'gold',
  onValaszt,
  fejlec = 'Új állandó felvétele',
  bovitett = false,
}: {
  fizetesiLehetosegek: Array<{ ertek: string; cimke: string }>;
  gombCimke: string;
  gombStilus?: 'gold' | 'pri';
  onValaszt: (etterem: EtteremSor, ertekek: KeresoErtekek) => Promise<void>;
  fejlec?: string;
  /**
   * A járatszervezés kártyája: a hordó és az F+ jelölő is megadható.
   * (javitasok-2.0-teljes.md 2. és 3.) Az állandó címeknél ezek nincsenek.
   */
  bovitett?: boolean;
}) {
  const [kereses, setKereses] = useState('');
  const [talalatok, setTalalatok] = useState<EtteremSor[]>([]);
  const [valasztott, setValasztott] = useState<EtteremSor | null>(null);
  const [ertekek, setErtekek] = useState<KeresoErtekek>({ ...URES, fizetesi_mod: '' });
  const [ment, setMent] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);

  useEffect(() => {
    const q = kereses.trim();
    if (q.length < 2) {
      setTalalatok([]);
      return;
    }
    const idozito = setTimeout(async () => {
      try {
        /*
         * Csak aktív partnert ajánlunk fel: az inaktív nem kerülhet járatra,
         * állandó címre vagy szállítólevélre. (2026-10-14, a megrendelő kérése)
         */
        const eredmeny = await api.get<EtteremLista>(
          `/api/etterem?q=${encodeURIComponent(q)}&limit=25&csak_aktiv=1`,
        );
        setTalalatok(eredmeny.sorok);
      } catch {
        setTalalatok([]);
      }
    }, 200);
    return () => clearTimeout(idozito);
  }, [kereses]);

  function valaszt(e: EtteremSor) {
    setValasztott(e);
    setHiba(null);
    // A fizetési mód alapból az étteremé — csak ha eltér, akkor kell átállítani.
    setErtekek({ ...URES, fizetesi_mod: e.fizetesi_mod ?? '' });
  }

  function bezar() {
    setValasztott(null);
    setKereses('');
    setTalalatok([]);
    setHiba(null);
  }

  async function felvesz() {
    if (!valasztott) return;
    setMent(true);
    setHiba(null);
    try {
      await onValaszt(valasztott, ertekek);
      bezar();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A felvétel nem sikerült.');
    } finally {
      setMent(false);
    }
  }

  const fpluszAr = valasztott?.fplusz_brutto ?? 0;

  return (
    <div className="side">
      <p className="lbl">{fejlec}</p>
      <input
        className="qi"
        placeholder="étterem keresése név vagy cím alapján…"
        autoComplete="off"
        value={kereses}
        onChange={(e) => setKereses(e.target.value)}
      />

      {valasztott && (
        <div className="addcard">
          <h4>{valasztott.nev}</h4>
          <div className="c">
            {[valasztott.irsz, valasztott.cim].filter(Boolean).join(' ') || '— nincs cím —'}
            {valasztott.brutto_ar ? (
              <> · {ft(valasztott.brutto_ar)} Ft/l</>
            ) : (
              <>
                {' · '}
                <span className="only">csak gyűjtés — MO/FO</span>
              </>
            )}
          </div>

          <div className={`qgrid${bovitett ? ' q4' : ''}`}>
            <div>
              <label>MO db</label>
              <input
                placeholder="0"
                value={ertekek.mo}
                onChange={(e) => setErtekek({ ...ertekek, mo: e.target.value })}
              />
            </div>
            <div>
              <label>FO db</label>
              <input
                placeholder="0"
                value={ertekek.fo}
                onChange={(e) => setErtekek({ ...ertekek, fo: e.target.value })}
              />
            </div>
            {bovitett && (
              <div>
                <label>Hordó db</label>
                <input
                  placeholder="0"
                  title="Kihelyezendő hordó — a járatlapon az étterem neve mellett „+N HO” jelzi."
                  value={ertekek.hordo}
                  onChange={(e) => setErtekek({ ...ertekek, hordo: e.target.value })}
                />
              </div>
            )}
            <div>
              <label>Mennyiség</label>
              <input
                placeholder="0"
                autoFocus
                value={ertekek.mennyiseg}
                onChange={(e) => setErtekek({ ...ertekek, mennyiseg: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && void felvesz()}
              />
            </div>
          </div>

          {bovitett && (
            <label
              className={`fpchk${fpluszAr > 0 ? '' : ' tiltott'}`}
              title={
                fpluszAr > 0
                  ? 'F+ olaj: a sorra a partner F+ ára kerül.'
                  : 'Ennek a partnernek nincs F+ ára a törzsben, ezért F+ olaj nem vehető fel.'
              }
            >
              <input
                type="checkbox"
                disabled={fpluszAr <= 0}
                checked={ertekek.fplusz}
                onChange={(e) => setErtekek({ ...ertekek, fplusz: e.target.checked })}
              />{' '}
              F+ olaj{' '}
              <span className="c">
                {fpluszAr > 0 ? `— ${ft(fpluszAr)} Ft/l` : '— nincs F+ ára'}
              </span>
            </label>
          )}

          <select
            value={ertekek.fizetesi_mod}
            onChange={(e) => setErtekek({ ...ertekek, fizetesi_mod: e.target.value })}
          >
            <option value="">Készpénz</option>
            {fizetesiLehetosegek.map((l) => (
              <option key={l.ertek} value={l.ertek}>
                {l.cimke}
              </option>
            ))}
          </select>

          {hiba && <div className="mezohiba">{hiba}</div>}

          <div className="btns">
            <button
              className={`btn ${gombStilus} sm`}
              disabled={ment}
              onClick={() => void felvesz()}
            >
              {ment ? 'Mentés…' : gombCimke}
            </button>
            <button className="btn sm" onClick={bezar}>
              Mégse
            </button>
          </div>
        </div>
      )}

      <div className="hits">
        {kereses.trim().length < 2 && <div className="empty">Kezdj el gépelni</div>}
        {kereses.trim().length >= 2 && talalatok.length === 0 && (
          <div className="empty">Nincs találat</div>
        )}
        {talalatok.map((e) => (
          <div key={e.id} className="hit" onClick={() => valaszt(e)}>
            <b>{e.nev}</b>
            <div className="c">
              {[e.irsz, e.cim].filter(Boolean).join(' ') || '— nincs cím —'}
            </div>
            <div className="m">
              {e.brutto_ar ? (
                `${ft(e.brutto_ar)} Ft/l`
              ) : (
                <span className="only">csak gyűjtés</span>
              )}
              {e.fplusz_brutto ? ` · F+ ${ft(e.fplusz_brutto)}` : ''}
              {e.fizetesi_mod ? ` · ${e.fizetesi_mod}` : ' · KP'}
            </div>
            {e.telephelyek > 1 && (
              <span className="tag">{e.cegnev ?? '—'} · {e.telephelyek} telephely</span>
            )}
            {e.megjegyzes && <div className="c">{e.megjegyzes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
